
import { Call } from "./Call";
import { Agent, ScaleResolutionDown } from "./Agent";
import * as anCti from "./ancti";

const STREAM_WEIGHT = ["video1", "video2", "video3", "display1" ];

export class ParticipantStreamStats {
    jitter = 0;
    packetsLost = 0;
    packetsReceived = 0;
    packetLoss = 0;
    quality = 0;
    frameRate = 0;
    bytesReceived = 0;
    bitRate = 0;
    resolution = "";
    timestamp = 0;
}

export type ConferenceListener = (conference: Conference) => void;

export class Conference implements anCti.Conference {
    
    public sharingVideo: boolean|MediaTrackConstraints = false;
    public sharingDisplay: boolean|MediaTrackConstraints = false;

    name?: string;
    participants: Participant[] = [];
    call: Call;

    agent: Agent;
    focus?: Participant;
    muted = false;
    ended = false;

    localParticipant: LocalParticipant;
    remoteParticipants: RemoteParticipant[] = [];
    
    joinCount = 0;
    maxVideoQuality = 3;
    subscriptions?: any[];
    videoStream?: MediaStream;
    displayStream?: MediaStream;

    onupdate?: (conference: anCti.Conference) => void;
    
    constructor(call:Call,participantID: string) {
        this.call = call;
        this.agent = call.agent;
        this.localParticipant = new LocalParticipant(this,participantID);
        this.localParticipant.displayName = `${this.agent.config?.firstName || ''} ${this.agent.config?.lastName || ''}`.trim();
    }

    stringify() {
		return `[Conference ${this.call.callID}]`;
	}

    public join(options?: anCti.ConferenceJoinOptions) {
        if (options?.displayName) {
            this.localParticipant.displayName = options.displayName;
        }
        this.updateJoin(true);
    }

    public setFocus(participant?:Participant) {
        if (this.focus!=participant) {
            this.focus = participant;
            this.participants.forEach(p => p.focus=(p==this.focus));
            this.updateSubscriptions();
        }
    }

    public getFocus(): Participant|undefined {
        return this.focus;
    }

    public getCall(): Call {
        return this.call;
    }

    public getParticipants(): Participant[] {
        return this.participants;
    }

    public getParticipant(id: string): Participant|undefined {
        return this.participants.find(p => p.id==id);
    }

    public getLocalParticipant() {
        return this.localParticipant;
    }

    public getName(): string {
        return this.name || "";
    }

    public setMuted(muted:boolean) {
        this.call.setMuted(muted);
        if (muted!=this.muted) {
            this.muted = muted;
        
            // we don't get an update for our own change and have to set it manually
            this.localParticipant.muted = muted;

            this.updateJoin(false);
        }
    }

    processUpdate(content: any) {
        if (content.participants) {
            if ( this.localParticipant && (this.localParticipant.video || this.localParticipant.display ) ) {
                let allStreamsClosed = true;
                for (const stream of this.localParticipant.localStreams) {
                    if (stream.mediaStream != undefined) {
                        allStreamsClosed = false;
                        break;
                    }
                }
                if ( allStreamsClosed ) {
                    this.restartStreams();
                    return;
                }
            }


            // update name
            this.name = content.name || this.name;
        
            // keep list of current participants
            let obsoleteParticipants = this.remoteParticipants.slice();

            for (const participantConfig of content.participants) {
                if (participantConfig.id==this.localParticipant.id) {
                    this.localParticipant.processUpdate(participantConfig);
                } else {
                    let remoteParticipant = this.remoteParticipants.find(m => m.id==participantConfig.id);
                    if (!remoteParticipant) {
                        remoteParticipant = new RemoteParticipant(this,participantConfig.id);
                        this.remoteParticipants.push(remoteParticipant);
                    } else {
                        // participant isn't obsolete
                        obsoleteParticipants = obsoleteParticipants.filter(p => p!=remoteParticipant);
                    }
                    remoteParticipant.processUpdate(participantConfig);
                }
            }

            // now cleanup obsolete participants
            obsoleteParticipants.forEach(p => p.close());
            this.remoteParticipants = this.remoteParticipants.filter(p => !obsoleteParticipants.includes(p)); 

            // build list of all participants
            if (this.localParticipant) {
                this.participants = [this.localParticipant,...this.remoteParticipants];
            }
    
            this.updateSubscriptions();

            this.onupdate?.(this);
        } else {
            this.agent.debug("closing empty conference");
            this.close();
        }

        this.agent.notify("conference",{
            name: "ConferenceEvent",
            call: this.call,
            device: this.call.device,
            conference: this,
        });
    }

    processStream(content: any) {
        const streamID: string = content.streamID;
        if (streamID.startsWith("video") || streamID.startsWith("display")) {
            this.localParticipant.processStream(content);
            return;
        } 
        let remoteParticipant = this.remoteParticipants.find(p => p.id==streamID); 
        if (remoteParticipant) {
            remoteParticipant.processStream(content);
            return;
        }
        this.agent.error("unknown stream",streamID);
    }

    // invoked by call when it is deleted
    close() {
        this.ended = true;
        this.participants.forEach(participant => participant.close());
        this.participants = [];
        this.remoteParticipants = [];
        this.videoStream?.getTracks().forEach(track => track.stop());
        this.displayStream?.getTracks().forEach(track => track.stop());
        this.videoStream = undefined;
        this.displayStream = undefined;
        this.sharingDisplay = false;
        this.sharingVideo = false;
        this.onupdate?.(this);
    }

    // checks if we need to update our subscriptions on the server
    updateSubscriptions() {
        const peers: any = {};
        let needsUpdate = false;
        for (const participant of this.remoteParticipants) {
            const before = participant.requestedQuality;
            if (!participant.video && !participant.display) {
                participant.requestedQuality = 0;
            } else if (!this.focus) {
                // noone has focus
                participant.requestedQuality = 3; // attempt to get the best quality
            } else if (participant==this.focus) {
                // participant has focus
                participant.requestedQuality = 3;
            } else {
                // other participant has focus
                participant.requestedQuality = 1;
            }
            needsUpdate ||= participant.requestedQuality!=before;
            peers[participant.id] = participant.requestedQuality;
        }

        if (needsUpdate) {
            if (this.joinCount) {
                this.joinCount++;
                this.agent.send({ConferenceJoin: {
                    connection: {
                        callID: this.call.callID,
                        deviceID: this.call.device.deviceID,
                    },
                    subscribe: peers,
                }});
            } else {
                // store for later use
                this.subscriptions = peers;
            }
        }
    }


    public async share(options: anCti.ConferenceShareOptions) {
        let changed = false;
        const obsoleteStreams: MediaStream[] = [];
        const display = options?.display;
        let video = options?.video;
        let newMediaStream: MediaStream|undefined;

        if (display) {
            try {
                newMediaStream = await this.agent.getDisplayMedia(this.call,{ video: display });
            } catch(error) {
                this.agent.info("cannot get getDisplayMedia",error);
                // abort share and tell listener
                this.onupdate?.(this);
                return;
            }
            this.displayStream && obsoleteStreams.push(this.displayStream);
            this.displayStream = newMediaStream;
            changed = true;
        } else if (this.displayStream) {
            obsoleteStreams.push(this.displayStream);
            this.displayStream = undefined;
            changed = true;
        }

        if (video && !newMediaStream) {
            try {
                newMediaStream = await this.agent.getUserMedia(this.call,{ video });
            } catch(error) {
                this.agent.info("cannot getUserMedia",error);
            }
            this.videoStream && obsoleteStreams.push(this.videoStream);
            this.videoStream = newMediaStream;
            changed = true;
        } else if (this.videoStream) {
            obsoleteStreams.push(this.videoStream);
            this.videoStream = undefined;
            changed = true;
        }
        
        // activate new stream
        this.sharingVideo = video || false;
        this.sharingDisplay = display || false;
        this.localParticipant.video = !!this.sharingVideo;
        this.localParticipant.display = !!this.sharingDisplay;
        this.localParticipant.updateMediaStream(newMediaStream!!);

        // close obsolete streams after setting up the new ones
        obsoleteStreams.forEach(stream => stream.getTracks().forEach(track => track.stop()));
        
        changed && this.updateJoin(false);
    }

  
    updateJoin(force:boolean) {
        if (this.joinCount || force) {
            let content: any = {
                connection: {
                    callID: this.call.callID,
                    deviceID: this.call.device.deviceID,
                },
                publish: {
                    video: this.videoStream?this.maxVideoQuality:0,
                    display: this.displayStream?1:0,
                    muted: this.muted,
                }
            };
            if (!this.joinCount) {
                content.publish.name = this.localParticipant.displayName;
            }
            if (this.subscriptions) {
                content.subscribe = this.subscriptions;
                this.subscriptions = undefined;
            } 
            this.joinCount++;
            this.agent.send({ConferenceJoin: content});
        }
    }

    restartStreams() {
        this.agent.send({ConferenceJoin: {
            connection: {
                callID: this.call.callID,
                deviceID: this.call.device.deviceID,
            },
            restart: "streams"
        }});
    }

}
 

export class Participant implements anCti.ConferenceParticipant {
    
    public id: string;
    public displayName = "";
    public avatar?: string;
    public muted = false;
    public focus = false;
    public local = false;
    public video = false;
    public display = false;
    public mediaStream?: MediaStream;
    public onstream?: (stream?:MediaStream) => void;
    public conference: Conference;

    closed = false;
    
    constructor(conference: Conference, id: string) {
        this.conference = conference;
        this.id = id;
    }

    processUpdate(config:any) {
        if (this.closed) throw "stream already closed";

        this.displayName = config.name || this.displayName;
        this.avatar = config.avatar || this.avatar;
        this.muted = config.muted;
        this.video = !!config.video;
        this.display = !!config.display;
    }

    setStream(mediaStream?: MediaStream) {
        if (this.closed) throw "Participant already closed";

        if (mediaStream!=this.mediaStream) {
            this.mediaStream = mediaStream;
            if (mediaStream) {
                mediaStream.getVideoTracks()[0].onended = () => this.onMediaEnded(mediaStream);
            }
            this.onstream?.(this.mediaStream);
        }
    }

    onMediaEnded(mediaStream: MediaStream) {
        if (!this.closed && mediaStream==this.mediaStream) {
            this.conference.share({video: false, display: false});
            this.onstream?.();
        }
    }

    close() {
        if (this.closed) throw "stream already closed";
        this.closed = true;
    }

}

export class LocalParticipant extends Participant {
    
    localStreams: ParticipantSendStream[] = [];
    
    constructor(conference: Conference, id: string) {
        super(conference,id);
        this.local = true;
    }

    processStream(content: any) {
        if (this.closed) throw "LocalParticipant already closed";

        const streamID = content.streamID;
        let localStream = this.localStreams.find(s => s.id==streamID);
        if (!localStream) {
            localStream = new ParticipantSendStream(this,streamID);
            this.localStreams.push(localStream);
        }
        localStream.processStream(content);
    }

    updateMediaStream(newMediaStream: MediaStream) {
        if (this.closed) throw "LocalParticipant already closed";

        for (const localStream of this.localStreams) {
            localStream.updateMediaStream(newMediaStream);
        }
    }

    removeLocalStream(stream: ParticipantSendStream) {
        this.localStreams = this.localStreams.filter(s => s!=stream);
    }

    setStream(_?:MediaStream) {
        if (this.closed) throw "LocalParticipant already closed";

        // select which one of our local-streams is best
        let bestStream: MediaStream|undefined;
        let bestWeight = -1;
        for (const localStream of this.localStreams) {
            if (localStream.mediaStream) {
                let weight = STREAM_WEIGHT.indexOf(localStream.id);
                if (weight>bestWeight) {
                    bestWeight = weight;
                    bestStream = localStream.mediaStream;
                }
            }
        }
        super.setStream(bestStream);
    }

    close() {
        if (this.closed) throw "LocalParticipant already closed";

        this.localStreams.forEach(stream => stream.close());
        this.localStreams = [];
        super.close();
    }

}

export class RemoteParticipant extends Participant {
   
    remoteStream: ParticipantReceiveStream|undefined;
    requestedQuality = 0;

    processStream(content: any) {
        if (this.closed) throw "RemoteParticipant already closed";

        const streamID = content.streamID;
        if (!this.remoteStream) {
            this.remoteStream = new ParticipantReceiveStream(this,streamID);
        }
        this.remoteStream.processStream(content);
    }

    async getStreamStats(): Promise<ParticipantStreamStats|undefined> {
        if (this.closed) throw "RemoteParticipant already closed";

        return this.remoteStream?.getStreamStats();
    }
    
    close() {
        if (this.closed) throw "RemoteParticipant already closed";

        this.remoteStream?.close();
        super.close();
    }

}

class ParticipantStream {

    id: string;
    pc: RTCPeerConnection;
    participant: Participant;
    mediaStream?: MediaStream;
    agent: Agent;
    closed = false;

    constructor(participant: Participant, id: string) {
        this.participant = participant;
        this.id = id;
        this.agent = participant.conference.agent;
        this.pc = new RTCPeerConnection();
    }

    processStream(content: any) {
        // abstract
    }

    public close() {
        if (this.closed) throw "ParticipantStream already closed";

        this.closed = true;
        this.pc.close();
    }

    async getStreamStats(): Promise<ParticipantStreamStats|undefined> {
        if (this.closed) throw "ParticipantStream already closed";

        return new Promise((resolve,reject) => {
            this.pc?.getStats(null).then((stats) => {
                let report: RTCInboundRtpStreamStats|undefined;
                stats?.forEach(r => {
                    if (r.type=="inbound-rtp" && r.kind=="video") {
                        report = r;
                    }
                });
                if (report) {
                    const newStats = new ParticipantStreamStats();
                    // calculate new values
                    newStats.timestamp = Date.now();
                    newStats.packetsReceived = report.packetsReceived || 0;
                    newStats.packetsLost = report.packetsLost || 0;
                    newStats.jitter = report.jitter || 0;
                    newStats.frameRate = report.framesPerSecond ||0;
                    newStats.bytesReceived = report.bytesReceived || 0;
                    newStats.resolution = (report.frameWidth || report.frameHeight) ? `${report.frameWidth}x${report.frameHeight}`: '-';

                    // calculate loss
                    if (newStats.packetsReceived+newStats.packetsLost > 0) {
                        newStats.packetLoss = Math.round(100 * newStats.packetsLost / (newStats.packetsReceived+newStats.packetsLost));
                    } else {
                        newStats.packetLoss = 100;
                    }

                    // calculate quality based on packetLoss
                    if (newStats.packetLoss<2) {
                        newStats.quality = 100;
                    } else if (newStats.packetLoss<4) {
                        newStats.quality = 80;
                    } else if (newStats.packetLoss<6) {
                        newStats.quality = 60;
                    } else if (newStats.packetLoss<8) {
                        newStats.quality = 40;
                    } else if (newStats.packetLoss<12) {
                        newStats.quality = 20;
                    } else {
                        newStats.quality = 0;
                    }
                    resolve(newStats);
                } else {
                    resolve(undefined);
                }
            
            }).catch(error => {
                reject(error);
            });
        });    
    } 
   
}

class ParticipantSendStream extends ParticipantStream {
    
    localParticipant: LocalParticipant;
    sender?: RTCRtpSender;

    constructor(localParticipant: LocalParticipant, id: string) {
        super(localParticipant,id);
        this.localParticipant = localParticipant;
    }
    
    async processStream(content: any) {
        if (this.closed) throw "ParticipantSendStream already closed";

        const direction: string = content.direction;
        
        if (direction=="close") {
            this.close();
            return;
        }

        if (direction=="restart") {
            this.pc?.restartIce();
        } else {
            if (!this.mediaStream) {
                if (this.id.startsWith("display")) {
                    this.updateMediaStream(this.participant.conference.displayStream);
                } else {
                    this.updateMediaStream(this.participant.conference.videoStream);
                }
            }
            this.participant.setStream(this.mediaStream);
        }

        const sdp = new RTCSessionDescription({type: "offer", sdp: content.sdp});
        this.pc.setRemoteDescription(sdp);
        await this.pc.setLocalDescription();
        if (this.closed) return;
    
        // apply encoding-parameters
        const scaleResolutionDown:any = this.agent.scaleResolutionDown[this.id as keyof ScaleResolutionDown];
        if (scaleResolutionDown) {
            this.pc.getSenders().forEach(sender => {
                const params = sender.getParameters();
                if (params?.encodings?.[0]) {
                    params.encodings[0].scaleResolutionDownBy = scaleResolutionDown;
                    sender.setParameters(params);
                }
            });
        }

        this.agent.send({ConferenceStreamAnswer: {
            connection: content.connection,
            streamID: this.id,
            sdp: this.pc.localDescription?.sdp,
        }});
    }

    updateMediaStream(mediaStream?:MediaStream) {
        if (this.closed) throw "ParticipantSendStream already closed";

        try {
            if (mediaStream!=this.mediaStream) {
                this.mediaStream = mediaStream;
                if (this.mediaStream) {
                    for (const track of this.mediaStream.getVideoTracks()) {
                        if (this.sender) {
                            this.sender.replaceTrack(track);
                        } else {
                            this.sender = this.pc.addTrack(track,this.mediaStream);
                        }
                        this.participant.setStream(this.mediaStream);
                        return;
                    }
                } 
                this.sender?.replaceTrack(null);
                this.participant.setStream(this.mediaStream);
            }
        } catch(error) {
            this.agent.error("cannot update stream",error);
        }
    }

    close() {
        if (this.closed) throw "ParticipantSendStream already closed";

        this.localParticipant.removeLocalStream(this);
        this.sender = undefined;
        super.close();
    }
}

class ParticipantReceiveStream extends ParticipantStream {

    remoteParticipant: RemoteParticipant;

    constructor(remoteParticipant: RemoteParticipant, id: string) {
        super(remoteParticipant,id);
        this.remoteParticipant = remoteParticipant;
    }

    async processStream(content: any) {
        if (this.closed) throw "ParticipantReceiveStream already closed";

        const direction: string = content.direction;
        
        if (direction=="close") {
            this.agent.debug("remote-party closed",this.id);
            this.close();
            return;
        } else if (direction=="restart") {
            this.pc?.restartIce();
        }

        this.pc.ontrack = (event:any) => {
            this.mediaStream = event.streams[0];
            event.track.addEventListener('unmute', () => {
                this.agent.debug("track unmuted",this.id);
                this.participant.setStream(this.mediaStream);
            });
            event.track.addEventListener('mute', () => {
                this.agent.debug("track muted",this.id);
            });
            this.participant.setStream(this.mediaStream);
        };
       
        const sdp = new RTCSessionDescription({type: "offer", sdp: content.sdp});
        this.pc.setRemoteDescription(sdp);
        await this.pc.setLocalDescription();
        if (this.closed) return;
 
        this.agent.send({ConferenceStreamAnswer: {
            connection: content.connection,
            streamID: this.id,
            sdp: this.pc.localDescription?.sdp,
        }});
    }

    public close() {
        if (this.closed) throw "ParticipantReceiveStream already closed";

        this.remoteParticipant.setStream(undefined);
        this.remoteParticipant.remoteStream = undefined;
        super.close();
    }

}

